import type {
  PortAvailabilityEpisode,
  ProductionHistoryWindow,
} from "@/features/devices/types";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import type { WidgetAvailabilityState } from "../../useAvailability";
import { faixasDaOnda } from "../../availabilityWave";
import { formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, HeroWindowPicker, type HeroTone } from "./HeroCard";

interface UtilizationWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  availability?: WidgetAvailabilityState;
  /**
   * A porta ancora declara `_v` no `/meta` atual?
   *
   * `false` significa que nao ha grandeza de movimento nesta porta — e
   * disponibilidade, aqui, E movimento. Ver o comentario no corpo.
   * `undefined` = ainda nao se sabe.
   */
  movimentoDeclarado?: boolean;
  editable?: boolean;
  onConfigure?: () => void;
  /** Desfaz o "Gerar grafico": so existe em card que nasceu de um. */
  onHideChart?: () => void;
  /** Troca a janela direto no card (persistida pelo pai). */
  onChangeWindow?: (window: ProductionHistoryWindow) => void;
}

const WINDOW_LABEL: Record<string, string> = {
  "6h": "6 h",
  "12h": "12 h",
  "24h": "24 h",
  "48h": "48 h",
  "7d": "7 dias",
  "30d": "30 dias",
};
/** Frase com concordância para o caption. */
const WINDOW_RECENT: Record<string, string> = {
  "6h": "nas últimas 6 h",
  "12h": "nas últimas 12 h",
  "24h": "nas últimas 24 h",
  "48h": "nas últimas 48 h",
  "7d": "nos últimos 7 dias",
  "30d": "nos últimos 30 dias",
};

// Onda quadrada: rodando no nível alto, parado/sem-sinal no baixo.
const VIEW_W = 600;
const VIEW_H = 46;
const PAD_X = 4;
const PLOT_W = VIEW_W - PAD_X * 2;
const Y_HIGH = 6; // nível "rodando" (topo da onda)
const Y_BASE = 34; // base da onda
const RAIL_Y = 37; // trilho de estado abaixo da base
/**
 * Colunas em que a onda e amostrada. Duas unidades de viewBox por coluna: com
 * `preserveAspectRatio="none"` o desenho encolhe para a largura do card, entao
 * mesmo no card mais estreito cada degrau ainda ocupa pelo menos um pixel de
 * verdade. Desenhar episodio a episodio dava 1213 degraus em 592 unidades.
 */
const COLUNAS_DA_ONDA = Math.round(PLOT_W / 2);
/** Abaixo disto, a amostra nao sustenta julgamento de desempenho da janela. */
const MIN_COVERAGE_PERCENT = 90;

/**
 * Disponibilidade no cartão herói (nova linguagem): a utilização % é o herói,
 * uma frase honesta diz o que aquilo significa, e a ONDA QUADRADA de
 * liga/desliga é a FACE embutida — com hover por episódio e as maiores
 * paradas. Sem caixa alta, sem pilha de badges.
 */
export function UtilizationWidget({
  widget,
  value,
  availability,
  movimentoDeclarado,
  editable,
  onConfigure,
  onHideChart,
  onChangeWindow,
}: UtilizationWidgetProps) {
  const status = availability?.status ?? "loading";
  // `reloading` = troca de janela: a onda/percentual da janela ANTERIOR fica
  // visível (atenuada) até a nova chegar.
  const busy = availability?.status === "reloading";
  const data =
    availability?.status === "ready" || availability?.status === "reloading"
      ? availability.data
      : null;
  const settled = status === "ready" || status === "reloading";
  const windowKey = widget.settings.history?.window ?? "24h";
  // Durante o reloading, o rótulo/eixos seguem a janela do DADO exibido.
  const dataWindowKey = busy && data ? data.window : windowKey;
  const windowLabel = WINDOW_LABEL[dataWindowKey] ?? "24 h";

  /**
   * SEM GRANDEZA DE MOVIMENTO, NAO HA DISPONIBILIDADE A CALCULAR.
   *
   * A disponibilidade e definida por MOVIMENTO (`v`), e nao por energia: uma
   * maquina ligada e parada nao esta disponivel. Numa porta que nunca publica
   * `v` — uma entrada digital publica `{s, acc}` —, os agregados nao tem o que
   * contar como atividade, e a resposta que se forma e "0 %, janela inteira
   * parada".
   *
   * Esse zero e indistinguivel de uma parada real, e era exatamente assim que
   * o card aparecia: percentual zerado e a onda como uma linha reta no fundo,
   * lida como "o grafico nao apareceu". Dizer que nao da para medir e a unica
   * resposta honesta.
   */
  const semMovimento = movimentoDeclarado === false;

  const utilization =
    !semMovimento && data && data.utilizationPercent !== null
      ? Math.round(data.utilizationPercent)
      : null;
  // Cortes POR PROCESSO (config do widget); padrão 60/30. `warn < good`
  // garantido para não inverter as faixas.
  const good = widget.settings.utilization?.goodAtPercent ?? 60;
  const warnRaw = widget.settings.utilization?.warnAtPercent ?? 30;
  const warn = Math.min(warnRaw, good);
  const coveragePercent =
    data && data.windowSeconds > 0
      ? Math.max(0, Math.min(100, (data.observedSeconds / data.windowSeconds) * 100))
      : null;
  const insufficientCoverage =
    !semMovimento && coveragePercent !== null && coveragePercent < MIN_COVERAGE_PERCENT;
  const utilizationTone: "ok" | "warn" | "danger" = semMovimento
    ? "warn"
    : utilization === null
      ? "ok"
      : utilization >= good
        ? "ok"
        : utilization >= warn
          ? "warn"
          : "danger";
  // Cobertura ruim torna o resultado inconclusivo. Nao afirmamos nem "boa"
  // nem "baixa" utilizacao com base numa fracao pequena da janela.
  const tone: "ok" | "warn" | "danger" = insufficientCoverage
    ? "warn"
    : utilizationTone;
  const measuredAt = data?.to ?? value.measuredAt;
  const meaning: { tone: HeroTone; text: string } | null = semMovimento
    ? {
        tone: "warn",
        text: "Esta porta não publica velocidade — não há movimento a medir.",
      }
    : resolveMeaning(
        busy ? "ready" : status,
        utilization,
        utilizationTone,
        coveragePercent,
      );

  const fromMs = data ? new Date(data.from).getTime() : 0;
  const span = data ? Math.max(new Date(data.to).getTime() - fromMs, 1) : 1;
  const xAt = (iso: string) =>
    PAD_X + ((new Date(iso).getTime() - fromMs) / span) * PLOT_W;

  // `?? []` protege contra uma API anterior (sem `episodes`) durante o deploy.
  const episodes = data?.episodes ?? [];
  const topStops = [...episodes]
    .filter((episode) => episode.state === "stopped")
    .sort((a, b) => b.seconds - a.seconds)
    .slice(0, 3);

  // Onda quadrada PREENCHIDA: sobe ao nível "rodando", cai à base quando para.
  //
  // O traço vem das FAIXAS (uma amostra por coluna de pixel), e não episódio a
  // episódio: em produção a API devolve mais de mil episódios numa janela de
  // 24 h, quase todos com menos de um pixel de largura, e desenhá-los um a um
  // produzia um código de barras ilegível no lugar da onda. Os totais e a
  // porcentagem seguem vindo da API intactos — muda só a granularidade do
  // traço. Ver `availabilityWave.ts`.
  const levelY = (state: PortAvailabilityEpisode["state"]) =>
    state === "running" ? Y_HIGH : Y_BASE;
  const faixas = data ? faixasDaOnda(episodes, data.from, data.to, COLUNAS_DA_ONDA) : [];
  const xDaColuna = (coluna: number) => PAD_X + (coluna / COLUNAS_DA_ONDA) * PLOT_W;
  let areaPath = "";
  if (faixas.length > 0) {
    areaPath = `M ${xDaColuna(faixas[0].colunaInicial).toFixed(1)} ${Y_BASE}`;
    for (const faixa of faixas) {
      const y = levelY(faixa.state);
      areaPath += ` L ${xDaColuna(faixa.colunaInicial).toFixed(1)} ${y} L ${xDaColuna(faixa.colunaFinal + 1).toFixed(1)} ${y}`;
    }
    areaPath += ` L ${xDaColuna(faixas[faixas.length - 1].colunaFinal + 1).toFixed(1)} ${Y_BASE} Z`;
  }

  return (
    <HeroCard
      className="w-hero--utilization"
      title={widget.title}
      caption={`Movimento ${WINDOW_RECENT[dataWindowKey] ?? `nas últimas ${windowLabel}`}`}
      tone={tone}
      alarm={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onHideChart={onHideChart}
      footnote={
        settled && measuredAt
          ? `Atualizado ${formatWidgetRelative(measuredAt) ?? "agora"}`
          : null
      }
    >
      <div className="w-hero__value">
        {utilization === null ? "--" : utilization}
        <span className="w-hero__unit">%</span>
      </div>

      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      {semMovimento ? (
        <div className="w-hero__trend">
          <p className="w-hero__trend-empty">
            Disponibilidade é movimento, e esta porta declara só estado e
            totalizador. Ancore o card num sinal de velocidade para medir.
          </p>
        </div>
      ) : data ? (
        <>
          <div className={busy ? "util__stats w-hero__trend--busy" : "util__stats"}>
            <span className="util__stat util__stat--run">
              <strong>{formatDuration(data.runningSeconds)}</strong> rodando
            </span>
            <span className="util__stat util__stat--stop">
              <strong>{formatDuration(data.stoppedSeconds)}</strong> parada
            </span>
            {data.gapSeconds > 0 ? (
              <span className="util__stat util__stat--gap">
                <strong>{formatDuration(data.gapSeconds)}</strong> sem sinal
              </span>
            ) : null}
          </div>

          <div
            className={busy ? "w-hero__trend w-hero__trend--busy" : "w-hero__trend"}
            aria-busy={busy || undefined}
          >
            <div className="w-hero__trend-hd">
              <span>Liga/desliga · {windowLabel}</span>
              {onChangeWindow ? (
                <HeroWindowPicker
                  value={windowKey as ProductionHistoryWindow}
                  busy={busy}
                  onChange={onChangeWindow}
                  ariaLabel={`Período de ${widget.title}`}
                />
              ) : null}
            </div>
            <svg
              className="util__wave"
              viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
              preserveAspectRatio="none"
              role="img"
              aria-label={`Onda de liga/desliga · ${windowLabel}`}
            >
              <line className="util__wave-grid" x1={PAD_X} y1={Y_BASE} x2={VIEW_W - PAD_X} y2={Y_BASE} />
              <path className="util__wave-area" d={areaPath} />
              {faixas
                .filter((faixa) => faixa.state !== "running")
                .map((faixa) => (
                  <rect
                    key={`rail-${faixa.colunaInicial}`}
                    className={`util__rail util__rail--${faixa.state}`}
                    x={xDaColuna(faixa.colunaInicial)}
                    y={RAIL_Y}
                    width={Math.max(
                      xDaColuna(faixa.colunaFinal + 1) - xDaColuna(faixa.colunaInicial),
                      0.7,
                    )}
                    height={5}
                  />
                ))}
              {episodes.map((episode, index) => (
                <rect
                  key={`hit-${episode.from}-${index}`}
                  className="util__wave-hit"
                  x={xAt(episode.from)}
                  y={0}
                  width={Math.max(xAt(episode.to) - xAt(episode.from), 0.7)}
                  height={VIEW_H}
                >
                  <title>{episodeTitle(episode, dataWindowKey)}</title>
                </rect>
              ))}
            </svg>

            <div className="util__axis">
              <span>{formatClock(data.from, dataWindowKey)}</span>
              <span className="util__legend">
                <i className="util__dot util__dot--run" /> rodando
                <i className="util__dot util__dot--stop" /> parada
                <i className="util__dot util__dot--gap" /> sem sinal
              </span>
              <span>{formatClock(data.to, dataWindowKey)}</span>
            </div>

            {topStops.length > 0 ? (
              <div className="util__stops">
                <span className="util__stops-label">Maiores paradas</span>
                {topStops.map((stop, index) => (
                  <span key={`stop-${index}`} className="util__stops-item">
                    {formatRange(stop, dataWindowKey)} · {formatDuration(stop.seconds)}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </>
      ) : (
        <div className="w-hero__trend">
          <p className="w-hero__trend-empty">
            {status === "unavailable"
              ? "Ative o Timescale para medir disponibilidade."
              : status === "error"
                ? "Não foi possível carregar a disponibilidade."
                : "Carregando disponibilidade…"}
          </p>
        </div>
      )}
    </HeroCard>
  );
}

function resolveMeaning(
  status: WidgetAvailabilityState["status"] | "loading",
  utilization: number | null,
  tone: "ok" | "warn" | "danger",
  coveragePercent: number | null,
): { tone: HeroTone; text: string } | null {
  if (status === "unavailable") {
    return { tone: "muted", text: "Disponibilidade requer Timescale ativo" };
  }
  if (status === "error") {
    return { tone: "warn", text: "Não foi possível ler a disponibilidade" };
  }
  if (status !== "ready") {
    return null;
  }
  if (coveragePercent !== null && coveragePercent < MIN_COVERAGE_PERCENT) {
    return {
      tone: "warn",
      text: `Cobertura de dados: ${Math.floor(coveragePercent)}% da janela`,
    };
  }
  if (utilization === null) return null;
  if (tone === "ok") return { tone: "ok", text: "Boa utilização do período" };
  if (tone === "warn") return { tone: "warn", text: "Utilização moderada" };
  return { tone: "danger", text: "Baixa utilização" };
}

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${total}s`;
}

const STATE_LABEL: Record<PortAvailabilityEpisode["state"], string> = {
  running: "rodando",
  stopped: "parada",
  gap: "sem sinal",
};

function episodeTitle(episode: PortAvailabilityEpisode, windowKey: string): string {
  return `${STATE_LABEL[episode.state]} ${formatRange(episode, windowKey)} · ${formatDuration(episode.seconds)}`;
}

function formatRange(episode: PortAvailabilityEpisode, windowKey: string): string {
  if (windowKey === "7d" || windowKey === "30d") {
    return `${formatClock(episode.from, windowKey)}–${formatClock(episode.to, windowKey)}`;
  }
  return `${formatHM(episode.from)}–${formatHM(episode.to)}`;
}

function formatHM(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatClock(iso: string, windowKey: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value: number) => String(value).padStart(2, "0");
  const dayMonth = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
  if (windowKey === "7d" || windowKey === "30d") return dayMonth;
  return `${dayMonth} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
